import {
  postGameByGameIdEndMutation,
  postGameStartMutation,
  type PostGameStartResponse,
} from "@aura-frontend/omnibus-az-client";
import { useMutation } from "@tanstack/react-query";
import { toast } from "@win-reality/react-ui";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

import { Spinner } from "@/components/ui/spinner";
import { getErrorDetail } from "@/utils/errorUtils";

import {
  type TeamSelectData,
  TeamSelectModal,
} from "../../../GameSetup/TeamSelectModal";
import { useGetActiveGame } from "../../../hooks/useGetActiveGame";
import {
  setBattingPracticeActive,
  setBattingPracticeRoster,
  setBattingPracticeTeamName,
} from "../../../store/slices/battingPractice";
import { setSessionType, setSessionUuid } from "../../../store/slices/session";
import {
  setCurrentFilter,
  setTrackingState,
} from "../../../store/slices/socket";
import { setActiveView } from "../../../store/slices/view";
import type { RootState } from "../../../store/store";
import { mapIPlayerToLineupEntry } from "../../../utils/api-helpers";
import { useUpdateGameState } from "../../../utils/useUpdateGameState";
import { resumeGameEffects } from "../../modals/ActiveGameModal/helpers";
import { BattingPracticeView } from "./BattingPracticeView";

/**
 * BattingPracticeModeView - Main container for Batting Practice mode
 *
 * On mount, checks if there's an active batting practice on this machine.
 * - If an active BP exists, auto-resumes it (rehydrates Redux state).
 * - If no active BP, sets up session defaults.
 *
 * Renders BattingPracticeView once isBattingPracticeActive is true in Redux.
 * Also manages socket event filtering for hit data.
 */
const BattingPracticeModeView = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { id: machineId } = useParams() as { id: string };
  const { fetchActiveGame } = useGetActiveGame();
  const updateGameState = useUpdateGameState();
  const [isCheckingActiveGame, setIsCheckingActiveGame] = useState(true);
  const [showTeamSelect, setShowTeamSelect] = useState(false);

  const isBattingPracticeActive = useSelector(
    (state: RootState) => state.battingPractice.isBattingPracticeActive,
  );

  const { mutateAsync: endGameMutation } = useMutation(
    postGameByGameIdEndMutation(),
  );

  const { mutate: startBPMutation, isPending: isPendingBP } = useMutation({
    mutationFn: postGameStartMutation().mutationFn,
    onError: (error: Error) => {
      const errorMessage = getErrorDetail(error);
      toast(`Error starting batting practice: ${errorMessage}`, {
        variant: "error",
      });
    },
  });

  const handleStartBattingPractice = async (data: TeamSelectData) => {
    // Defensively end any lingering active game before starting a new one.
    try {
      const activeGame = await fetchActiveGame();
      if (activeGame?.game) {
        await endGameMutation({
          body: {},
          path: {
            // @ts-expect-error gameUuid is not correctly typed in the API spec.
            game_id: activeGame.game.gameUuid,
          },
        });
      }
    } catch {
      // Previous session could not be ended — don't try to start a new one
      toast("Failed to end the previous session. Please try again.", {
        variant: "error",
      });
      return;
    }

    startBPMutation(
      {
        body: {
          type: "batting_practice",
          machineUuid: machineId,
          awayTeamLineup: [],
          homeTeamId: data.teamUuid,
          homeTeamLineup: data.teamRoster.map((player, i) =>
            mapIPlayerToLineupEntry(player, i + 1),
          ),
        },
      },
      {
        onSuccess: (data2: PostGameStartResponse) => {
          if (!data2.gameState || !data2.game) {
            throw new Error("Missing gameState or game in response");
          }

          dispatch(setBattingPracticeTeamName(data.teamName));
          dispatch(setBattingPracticeRoster(data.teamRoster));
          dispatch(setBattingPracticeActive(true));
          dispatch(setSessionUuid(data2.game.gameUuid));
          dispatch(setSessionType("batting_practice"));
          dispatch(setTrackingState(true));
          dispatch(setActiveView("Batting Practice Mode"));
          setShowTeamSelect(false);
        },
      },
    );
  };

  // On mount, check for an active batting practice and auto-resume if found
  useEffect(() => {
    // Branch 1: Already active (resume path or localStorage)
    if (isBattingPracticeActive) {
      setIsCheckingActiveGame(false);
      return;
    }

    // Branch 2: Navigated from Welcome — skip API check, show setup
    if ((location.state as { showSetup?: boolean })?.showSetup) {
      dispatch(setSessionType("batting_practice"));
      dispatch(setTrackingState(true));
      dispatch(setActiveView("Batting Practice Mode"));
      setShowTeamSelect(true);
      setIsCheckingActiveGame(false);
      // Clear state so page refresh does a full check
      window.history.replaceState({}, "");
      return;
    }

    // Branch 3: Direct navigation/refresh — full API check (existing logic)
    const checkAndResumeBP = async () => {
      const activeGamePayload = await fetchActiveGame();

      if (
        activeGamePayload?.game &&
        activeGamePayload.game.type === "batting_practice"
      ) {
        resumeGameEffects(dispatch, {
          game: activeGamePayload.game,
          awayTeamPlayers: activeGamePayload.awayTeamPlayers,
          homeTeamPlayers: activeGamePayload.homeTeamPlayers,
        });
        updateGameState(activeGamePayload.gameState, activeGamePayload.game);
        dispatch(setBattingPracticeActive(true));
      } else {
        // End any non-BP active game before showing team select
        if (activeGamePayload?.game) {
          try {
            await endGameMutation({
              body: {},
              path: {
                // @ts-expect-error gameUuid is not correctly typed in the API spec.
                game_id: activeGamePayload.game.gameUuid,
              },
            });
          } catch {
            // If ending fails, still proceed to show team select
          }
        }

        dispatch(setSessionType("batting_practice"));
        dispatch(setTrackingState(true));
        dispatch(setActiveView("Batting Practice Mode"));
        setShowTeamSelect(true);
      }

      setIsCheckingActiveGame(false);
    };

    checkAndResumeBP();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set socket filter to show only hit data events
  useEffect(() => {
    dispatch(setCurrentFilter("has_hit_data"));

    return () => {
      dispatch(setCurrentFilter("all"));
    };
  }, [dispatch]);

  if (isCheckingActiveGame) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner className="h-8 w-8" />
        <span className="ml-3 text-base text-muted-foreground">
          Loading batting practice...
        </span>
      </div>
    );
  }

  if (!isBattingPracticeActive) {
    return (
      <TeamSelectModal
        isOpen={showTeamSelect}
        onClose={() => setShowTeamSelect(false)}
        onSubmit={handleStartBattingPractice}
        title="Start Batting Practice"
        confirmLabel="Start Batting Practice"
        isConfirmPending={isPendingBP}
      />
    );
  }

  return <BattingPracticeView />;
};

export default BattingPracticeModeView;
